import { toast } from 'sonner'
import type {
  Collection,
  GetProductsOptions,
  Product,
  ProductsResponse,
} from '@/services/products/types'
import { BaseService } from '../base/BaseService'
import { sortColumnToWireName } from './sortColumns'

export interface ProductManagementOptions {
  onSuccess?: (message: string) => void
  onError?: (error: string) => void
  showToast?: boolean
}

export interface BulkDeleteOptions extends ProductManagementOptions {
  selectedProductIds: string[]
}

export interface ProductFilterOptions {
  collectionId?: string
  searchQuery?: string
  status?: string
  page?: number
  perPage?: number
}

class ProductManagementService extends BaseService {
  protected readonly baseURL = '/products'

  /**
   * Get paginated list of products
   */
  async getProducts(options: GetProductsOptions): Promise<ProductsResponse> {
    const {
      page,
      perPage,
      collectionId,
      searchQuery,
      sort,
      direction,
      status,
      issue,
    } = options
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    })

    if (collectionId) {
      params.append('collection_id', collectionId)
    }

    if (searchQuery && searchQuery.trim()) {
      params.append('search', searchQuery.trim())
    }

    if (sort) {
      params.append('sort', sortColumnToWireName(sort))
      params.append('direction', direction ?? 'desc')
    }

    if (status) {
      params.append('status', status)
    }

    if (issue) {
      params.append('issue', issue)
    }

    const response = await this.apiClient.get(`${this.baseURL}?${params}`)
    return response.data
  }

  async getProductTitles(
    collectionId: string,
    options?: {
      search?: string
      selectedIds?: string[]
      offset?: number
      limit?: number
    }
  ): Promise<{
    titles: { id: string; title: string; externalId?: string }[]
    totalCount: number
  }> {
    const params: Record<string, unknown> = {
      collection_id: collectionId,
    }

    if (options?.search) params.search = options.search
    if (options?.offset) params.offset = options.offset
    if (options?.limit) params.limit = options.limit

    if (options?.selectedIds?.length) {
      params['selected_ids[]'] = options.selectedIds.filter(Boolean)
    }

    const response = await this.apiClient.get(`${this.baseURL}/titles`, {
      params,
    })

    return {
      titles: response.data.titles || [],
      totalCount: response.data.totalCount ?? 0,
    }
  }

  /**
   * Create a new product in a collection
   */
  async createProduct(
    collectionId: string,
    data: Record<string, unknown>
  ): Promise<{ success: boolean; product?: Product; errors?: string[] }> {
    try {
      const response = await this.apiClient.post(this.baseURL, {
        product: { collection_id: collectionId, ...data },
      })
      return { success: true, product: response.data.product }
    } catch (error) {
      const err = error as { response?: { data?: { errors?: string[] } } }
      return {
        success: false,
        errors: err.response?.data?.errors ?? ['Failed to create product'],
      }
    }
  }

  /**
   * Update a single product field (for inline editing)
   */
  async updateProduct(
    productId: string,
    updates: Record<string, unknown>
  ): Promise<{ success: boolean; product?: Product; errors?: string[] }> {
    try {
      const response = await this.apiClient.patch(
        `${this.baseURL}/${productId}`,
        { product: updates }
      )
      return { success: true, product: response.data.product }
    } catch (error) {
      const err = error as { response?: { data?: { errors?: string[] } } }
      return {
        success: false,
        errors: err.response?.data?.errors ?? ['Failed to update product'],
      }
    }
  }

  /**
   * Upload an image for a product
   */
  async uploadProductImage(
    productId: string,
    file: File
  ): Promise<{
    success: boolean
    image?: { id: string; url: string; position: number; isPrimary: boolean }
    errors?: string[]
  }> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await this.apiClient.post(
        `${this.baseURL}/${productId}/images`,
        formData
      )
      return { success: true, image: response.data.image }
    } catch (error) {
      const err = error as { response?: { data?: { errors?: string[] } } }
      return {
        success: false,
        errors: err.response?.data?.errors ?? ['Failed to upload image'],
      }
    }
  }

  /**
   * Delete a single product with confirmation and error handling
   */
  async deleteProduct(
    product: Product,
    options: ProductManagementOptions = {}
  ): Promise<{ success: boolean; error?: string }> {
    const { onSuccess, onError, showToast = true } = options

    try {
      const result = await this.apiClient.delete(
        `${this.baseURL}/${product.id}`
      )

      if (result.data.success) {
        const message = `"${product.title}" has been deleted successfully.`
        if (showToast) toast.success(message)
        onSuccess?.(message)
        return { success: true }
      } else {
        throw new Error(result.data.error || 'Failed to delete product')
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred while deleting the product'
      if (showToast) toast.error(errorMessage)
      onError?.(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Duplicate a product with error handling
   */
  async duplicateProduct(
    product: Product,
    options: ProductManagementOptions = {}
  ): Promise<{
    success: boolean
    error?: string
    duplicatedProduct?: Product
  }> {
    const { onSuccess, onError, showToast = true } = options

    try {
      const result = await this.apiClient.post(
        `${this.baseURL}/${product.id}/duplicate`
      )

      if (result.data.success) {
        const message = `"${product.title}" has been duplicated successfully.`
        if (showToast) toast.success(message)
        onSuccess?.(message)
        return {
          success: true,
          duplicatedProduct: result.data.product,
        }
      } else {
        throw new Error(result.data.error || 'Failed to duplicate product')
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred while duplicating the product'
      if (showToast) toast.error(errorMessage)
      onError?.(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Bulk delete multiple products using efficient API endpoint
   */
  async bulkDeleteProducts(
    options: BulkDeleteOptions
  ): Promise<{ success: boolean; deletedCount: number; errors: string[] }> {
    const { selectedProductIds, onSuccess, onError, showToast = true } = options

    if (selectedProductIds.length === 0) {
      const errorMessage = 'No products selected for deletion'
      if (showToast) toast.error(errorMessage)
      onError?.(errorMessage)
      return { success: false, deletedCount: 0, errors: [errorMessage] }
    }

    try {
      const response = await this.apiClient.delete(
        `${this.baseURL}/bulk_destroy`,
        {
          data: { product_ids: selectedProductIds },
        }
      )

      const result = response.data

      if (result.success && result.deleted_count > 0) {
        const message =
          result.message ||
          `Successfully deleted ${result.deleted_count} product${result.deleted_count === 1 ? '' : 's'}`
        if (showToast) toast.success(message)
        onSuccess?.(message)
      }

      if (result.errors && result.errors.length > 0) {
        const errorMessage = `Some products could not be deleted: ${result.errors.join(', ')}`
        if (showToast)
          toast.warning('Partial deletion completed', {
            description: errorMessage,
          })
        onError?.(errorMessage)
      }

      return {
        success: result.success,
        deletedCount: result.deleted_count || 0,
        errors: result.errors || [],
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Bulk delete operation failed'
      if (showToast) toast.error(errorMessage)
      onError?.(errorMessage)
      return {
        success: false,
        deletedCount: 0,
        errors: [errorMessage],
      }
    }
  }

  /**
   * Validate file upload requirements
   */
  validateFileUpload(file: File): { isValid: boolean; error?: string } {
    const maxSize = 50 * 1024 * 1024 // 50MB

    // Validate file size
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`,
      }
    }

    // Validate file type
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    const mime = file.type.toLowerCase()
    const allowedExt = ['csv', 'txt']
    const allowedMime = ['text/csv', 'text/plain', 'application/vnd.ms-excel']

    if (!allowedExt.includes(ext) && !allowedMime.includes(mime)) {
      return {
        isValid: false,
        error: 'Please upload a CSV or TXT file',
      }
    }

    return { isValid: true }
  }

  /**
   * Get empty state message based on current filters
   */
  getEmptyStateMessage(searchQuery?: string, collection?: Collection): string {
    if (searchQuery) {
      return `No products found for "${searchQuery}"`
    }
    if (collection) {
      return `No products in "${collection.name}" collection`
    }
    return 'No products found'
  }
}

export const productManagementService = new ProductManagementService()
